import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Copy01, CurrencyDollar, DotsVertical, Edit01, Menu, Trash01 } from '@opendate-app/setlist';
import EditSeatingChartDesigner from './EditSeatingChartDesigner';
import SectionPricesModal from './SectionPricesModal';
import axios from 'axios';
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const SeatingChartCard = ({ seatingChart, onSelectChart, csrfToken, isAddOn, showSettings=false, onDuplicateComplete, onChartSynced, onChartDestroyed }) => {
  const [showDesigner, setShowDesigner] = useState(false);
  const [showSectionPrices, setShowSectionPrices] = useState(false);

  const handleDuplicate = (context) => {
    toastSuccess("Duplicating seating chart, please wait a moment...");
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/seating_charts/${seatingChart.id}/copy`, {
      context
    })
    .then(({ data }) => {
      onDuplicateComplete(data.seating_chart);
      toastSuccess(context === "add_on" && !isAddOn ? "Add-on map created. Find it under Add-on Seating Charts." : "Seating chart duplicated successfully!");
    })
    .catch((err) => {
      const message = err?.response?.data?.errors?.join(', ') || err?.response?.data?.error || 'There was an error duplicating the chart.';
      toastError(message);
    });
  };

  const handleDelete = () => {
    if (!window.confirm('Archive this seat map? It leaves this list. Past shows keep their tickets and seats.')) return;

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.delete(`/seating_charts/${seatingChart.id}`)
      .then(() => {
        toastSuccess('Seat map archived.');
        onChartDestroyed(seatingChart);
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.join(', ') || err?.response?.data?.error || 'There was an error archiving the seat map.';
        toastError(message);
      });
  };

  return (
    <>
      {
        showSettings && !seatingChart.tickets_or_reservations_exist &&

        <EditSeatingChartDesigner
          show={showDesigner}
          onClose={() => setShowDesigner(false)}
          seatingChart={seatingChart}
          onChartSynced={onChartSynced}
        />
      }
      {showSettings && (
        <SectionPricesModal
          open={showSectionPrices}
          onClose={() => setShowSectionPrices(false)}
          seatingChart={seatingChart}
          csrfToken={csrfToken}
        />
      )}
      <div className="col-12 col-xl-6">
        <Card>
          <div className="d-flex align-items-start" style={{ gap: 12 }}>
            <img src={seatingChart.preview_thumb_url}
                  className="img-fluid"
                  style={{
                    "width": "120px",
                    "height": "120px"
                  }} />
            <div className="flex-fill"
                  style={{
                    "color": "var(--sl-text-primary)",
                    "fontSize": "16px",
                    "fontWeight": 600,
                    "minWidth": 0
                  }}>
              {seatingChart.name}
            </div>
            {onSelectChart && (
              <Button
                size="sm"
                onClick={
                  (e) => {
                    e.preventDefault();
                    onSelectChart(seatingChart)
                  }
                }
              >
                Select Chart
              </Button>
            )}
            {showSettings && (
              <div onClick={(e) => e.stopPropagation()}>
                <Menu
                  placement="bottom-end"
                  trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="Seating chart actions" />}
                  items={[
                    {
                      icon: Copy01,
                      label: "Duplicate Seating Chart",
                      onClick: () => handleDuplicate(isAddOn ? "add_on" : "admission")
                    },
                    ...(isAddOn ? [] : [{
                      icon: Copy01,
                      label: "Copy as add-on map",
                      onClick: () => handleDuplicate("add_on")
                    }]),
                    {
                      icon: CurrencyDollar,
                      label: "Section Prices",
                      onClick: () => setShowSectionPrices(true)
                    },
                    {
                      disabled: seatingChart.tickets_or_reservations_exist,
                      icon: Edit01,
                      label: "Edit Seating Chart",
                      onClick: () => setShowDesigner(true)
                    },
                    {
                      icon: Trash01,
                      label: "Archive Seating Chart",
                      destructive: true,
                      onClick: handleDelete
                    }
                  ]}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  )

}

SeatingChartCard.propTypes = {
  seatingChart: PropTypes.object.isRequired,
  onSelectChart: PropTypes.func,
  csrfToken: PropTypes.string.isRequired,
  isAddOn: PropTypes.bool.isRequired,
  showSettings: PropTypes.bool,
  onDuplicateComplete: PropTypes.func,
  onChartSynced: PropTypes.func,
  onChartDestroyed: PropTypes.func,
};

export default SeatingChartCard;
